/* eslint-disable max-lines */
import React, { Component } from 'react';
import { t } from 'i18next';
import { shape, func, number, arrayOf, string, bool, oneOfType } from 'prop-types';
import { GoogleApiWrapper } from 'google-maps-react';
import { connect } from 'react-redux';

import { LANGUAGE, ISO_COUNTRY } from '~constants/search';
import { actionCreators as notificationActions } from '~redux/Notification/actions';
import { NOTIFICATION_DANGER } from '~redux/Notification/constants';
import { addressPropTypes } from '~propTypes/address';
import { parseLocation, filterUserAddresses } from '~utils/maps';
import SimpleSpinner from '~components/SimpleSpinner';

import {
  ERROR_TEXTS,
  DEFAULT_MAX_RESULTS,
  DEFAULT_EVENT_DELAY,
  MIN_LENGTH_TO_SEARCH,
  DELAY_SEARCH_TIME
} from './constants';
import { COUNTRY_SPLIT } from './utils';
import StoreSearchBar from './layout';

class StoreSearchBarContainer extends Component {
  state = {
    value: '',
    results: [],
    firstClicked: false,
    focused: false,
    loadingCurrentLocation: false,
    showResults: false,
    avoidBlur: false // eslint-disable-line react/no-unused-state
  };

  componentDidMount() {
    this.initService();
    const { incompleteAddress } = this.props;
    if (incompleteAddress) {
      this.handleInputChange({ target: { value: incompleteAddress.textAddress } }, 0);
    }
  }

  componentDidUpdate(prevProps) {
    const { incompleteAddress } = this.props;
    if (
      incompleteAddress !== undefined &&
      prevProps.incompleteAddress?.textAddress !== incompleteAddress.textAddress
    ) {
      this.handleInputChange({ target: { value: incompleteAddress.textAddress } }, 0);
    }
  }

  static getDerivedStateFromProps({ selectedAddress }) {
    if (selectedAddress?.textAddress) {
      return {
        value: selectedAddress.textAddress
      };
    }

    return null;
  }

  initService = () => {
    const { google, onInit } = this.props;

    if (google) {
      this.service = new google.maps.places.AutocompleteService();
      // TODO check onInit is used
      onInit?.(google);
    }
  };

  displaySuggestions = (suggestions, status) => {
    const { google } = this.props;
    let results = [];

    if (status === google.maps.places.PlacesServiceStatus.OK && suggestions.length) {
      results = suggestions.map(result => ({
        placeId: result.place_id,
        description: result.description.split(COUNTRY_SPLIT)[0]
      }));
    }
    this.setState({ results });
  };

  getCurrentLocation = () => {
    const { google, onAddressSelect } = this.props;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(location => {
        parseLocation({ google, location: location.coords }, addressData => {
          this.setState({
            value: addressData.formattedAddress,
            showResults: false,
            loadingResults: false,
            loadingCurrentLocation: false
          });
          onAddressSelect?.({ addressData });
        });
      }, this.showError);
    }
  };

  showError = error => {
    const { showNotification } = this.props;

    this.setState({
      showResults: false,
      loadingResults: false,
      loadingCurrentLocation: false
    });

    showNotification({
      message: ERROR_TEXTS[error.code] || t('SearchAddress:UNKNOWN'),
      type: NOTIFICATION_DANGER
    });
  };

  handleAcceptBlur = () => this.setState({ avoidBlur: false }); // eslint-disable-line react/no-unused-state

  handleAvoidBlur = () => this.setState({ avoidBlur: true }); // eslint-disable-line react/no-unused-state

  delayTimer = null;

  handleInputChange = (e, delaySearch = DELAY_SEARCH_TIME) => {
    const { value } = e.target;
    clearTimeout(this.delayTimer);
    if (value) {
      const { onChange } = this.props;

      this.setState({ value });
      onChange?.(value);
      this.delayTimer = setTimeout(() => this.handleSearch(value), delaySearch);
    } else {
      this.handleClean();
    }
    this.handleFocus();
  };

  handleSearch = value => {
    if (value.length >= MIN_LENGTH_TO_SEARCH) {
      this.setState({ showResults: true });
      const request = {
        input: value,
        componentRestrictions: { country: ISO_COUNTRY }
      };

      this.service.getPlacePredictions(request, (suggestions, status) =>
        this.displaySuggestions(suggestions, status)
      );
    }
  };

  handleResultSelect = ({ placeId, ...location }) => {
    const { google, onAddressSelect } = this.props;
    this.setState({ loadingResultData: true, focused: false });
    this.handleFocus();
    if (google) {
      if (placeId || (location.longitude && location.latitude)) {
        const stateValue = this.state.value;

        parseLocation({ google, placeId, location }, addressData => {
          const value = addressData?.textAddress || stateValue;

          this.setState({ value, loadingResults: false, loadingResultData: false, showResults: false });
          onAddressSelect?.({ addressData: { ...addressData, ...location } });
        });
      } else {
        this.setState({ loadingCurrentLocation: true, loadingResultData: false, showResults: false });
        this.getCurrentLocation();
      }
    }
    this.handleAcceptBlur();
  };

  handleClean = fromButton => {
    const { userAddresses, onClean } = this.props;

    this.setState({
      focused: !fromButton,
      value: '',
      results: fromButton ? [] : filterUserAddresses(userAddresses),
      showResults: !fromButton
    });
    onClean?.();
  };

  handleFocus = () => {
    const { userAddresses, onFocus } = this.props;
    const { value } = this.state;

    this.setState({ focused: true });

    if (!value) {
      this.setState({ showResults: true, results: filterUserAddresses(userAddresses) });
    }
    onFocus?.();
  };

  handleBlur = () => {
    const { onBlur } = this.props;
    const { value } = this.state;

    this.setState({ focused: false });

    // TODO: Find a better way to make this work. This is done to be able to get the target of the event before the component changes it's style and it's unavailable
    setTimeout(() => {
      if (!value) {
        this.setState({ showResults: false });
      }
      onBlur?.();
    }, DEFAULT_EVENT_DELAY);
  };

  handleFirstClick = event => {
    const { onFirstClick } = this.props;
    const { firstClicked } = this.state;

    if (!firstClicked) {
      this.setState({ firstClicked: true });
      onFirstClick?.(event.target);
    }
  };

  render() {
    const {
      value,
      focused,
      results,
      showResults,
      loadingResults,
      loadingResultData,
      loadingCurrentLocation
    } = this.state;
    const { maxResults, placeholder, placeholderClassName, iconClassName, className, simple } = this.props;

    return (
      <StoreSearchBar
        value={value}
        className={className}
        onBlur={this.handleBlur}
        onClean={this.handleClean}
        onChange={this.handleInputChange}
        onFocus={this.handleFocus}
        onFirstClick={this.handleFirstClick}
        onResultSelect={this.handleResultSelect}
        onResultsMouseOver={this.handleAvoidBlur}
        onResultsMouseOut={this.handleAcceptBlur}
        getCurrentLocation={this.getCurrentLocation}
        focused={focused}
        loadingResultData={loadingResultData}
        loadingCurrentLocation={loadingCurrentLocation}
        loadingResults={loadingResults}
        showResults={showResults}
        maxResults={maxResults}
        results={results}
        placeholder={placeholder}
        simple={simple}
        placeholderClassName={placeholderClassName}
        iconClassName={iconClassName}
      />
    );
  }
}

StoreSearchBarContainer.defaultProps = {
  maxResults: DEFAULT_MAX_RESULTS,
  placeholder: '',
  selectedAddress: false,
  simple: false
};

StoreSearchBarContainer.propTypes = {
  showNotification: func.isRequired,
  onAddressSelect: func.isRequired,
  className: string,
  google: shape({
    maps: shape({
      Geocoder: func.isRequired
    })
  }),
  iconClassName: string,
  incompleteAddress: oneOfType([addressPropTypes, undefined]),
  maxResults: number,
  placeholder: string,
  placeholderClassName: string,
  selectedAddress: oneOfType([addressPropTypes, bool]),
  simple: bool,
  userAddresses: arrayOf(addressPropTypes),
  onBlur: func,
  onChange: func,
  onClean: func,
  onFirstClick: func,
  onFocus: func,
  onInit: func
};

const mapDispatchToProps = dispatch => ({
  showNotification: value => dispatch(notificationActions.show(value))
});

const StoreSearchBarContainerConnect = connect(null, mapDispatchToProps)(StoreSearchBarContainer);

// eslint-disable-next-line
export default GoogleApiWrapper({
  apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  language: LANGUAGE,
  LoadingContainer: SimpleSpinner
})(StoreSearchBarContainerConnect);
